import IBaseListingResponse from "../../utils/api/base-listing-response";
import { HttpClient, RequestParams } from "../../utils/api/http-client";
import { IInventoryShortageQuery } from "../../models/wms/inventory-shortage/query";
import {
  ICorrectInventoryShortageReceiptRequest,
  ICoverInventoryShortageRequest,
} from "../../models/wms/inventory-shortage/request";
import { IInventoryShortage } from "../../models/wms/inventory-shortage/response";

/**
 * The endpoint reads filters through the shared `BaseFilter`
 * (`filters[n][name|value|operation]`), not flat query keys. `operation` is left
 * off on purpose: the backend defaults to equality, which is all this screen
 * needs, and omitting it keeps us clear of the `eq` / `=` vocabulary question.
 *
 * Every field name the API accepts appears exactly once here, so a rename on the
 * backend is a one-line change rather than a hunt through the page.
 */
const buildInventoryShortagesQuery = (
  query: IInventoryShortageQuery = {},
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};

  if (query.page !== undefined) params.page = query.page;
  if (query.perPage !== undefined) params.perPage = query.perPage;
  if (query.keyword) params.keyword = query.keyword;

  const filters: Array<[string, string | number | boolean]> = [];
  const add = (name: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return;
    filters.push([name, value as string | number | boolean]);
  };

  add("id", query.id);
  add("status", query.status);
  add("orderId", query.orderId);
  add("productOrderId", query.productOrderId);
  add("productId", query.productId);
  add("storeId", query.storeId);
  add("createdAt", query.createdAt);
  // Only ever sent when true; sending `false` would filter to resolved rows.
  if (query.unresolvedOnly) add("unresolvedOnly", true);

  filters.forEach(([name, value], index) => {
    params[`filters[${index}][name]`] = name;
    params[`filters[${index}][value]`] = value;
  });

  return params;
};

class WmsInventoryShortageService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  getAll = (query: IInventoryShortageQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IInventoryShortage>>({
      path: "/wms/inventory-shortages",
      method: "GET",
      query: buildInventoryShortagesQuery(query),
      ...params,
    });

  getOne = (id: number, params?: RequestParams) =>
    this.http.request<IInventoryShortage>({
      path: `/wms/inventory-shortages/${id}`,
      method: "GET",
      ...params,
    });

  cover = (
    id: number,
    data: ICoverInventoryShortageRequest,
    params?: RequestParams,
  ) =>
    this.http.request<IInventoryShortage>({
      path: `/wms/inventory-shortages/${id}/cover`,
      method: "POST",
      body: data,
      ...params,
    });

  correctReceipt = (
    id: number,
    data: ICorrectInventoryShortageReceiptRequest,
    params?: RequestParams,
  ) =>
    this.http.request<IInventoryShortage>({
      path: `/wms/inventory-shortages/${id}/receipt-correction`,
      method: "POST",
      body: data,
      ...params,
    });
}

export default WmsInventoryShortageService;
